import { getToken, removeToken, saveToken } from './../utils/tokenUtils';
import axios, { AxiosInstance } from 'axios';
const {
  REACT_APP_BASE_API_URL: DATA_URL,
  REACT_APP_BASE_API_URL_DEV: DATA_URL_DEV,
  REACT_APP_AUTH_API_URL: AUTH_URL,
  REACT_APP_AUTH_API_URL_DEV: AUTH_URL_DEV,
  REACT_APP_X_API_KEY: APIKEY,
} = process.env;

const isDev = process.env.NODE_ENV === 'development';
// DATA — country-scoped ресурси нового admin data-service (base = .../api/v1/).
const dataBaseURL = isDev ? DATA_URL_DEV : DATA_URL;
// AUTH — auth/profile/admin-users лишаються на попередньому беку (base = .../api/v1/admin/).
const authBaseURL = isDev ? AUTH_URL_DEV : AUTH_URL;

const commonHeaders = {
  Accept: 'application/json',
  crossDomain: true,
  'API-KEY': APIKEY,
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
  'Content-Type': 'application/json',
  'Accept-Language': 'en',
};

// Обрана в профілі країна — інжектиться у шлях кожного data-запиту.
let countryCode = '';
export const setCountryCode = (code: string) => {
  countryCode = code || '';
};

// data-клієнт: ресурси нового admin API. Шлях кожного запиту стає
// `{countryCode}/admin/<url>` (бек читає :countryCode зі шляху, не з хедера).
const dataClient = axios.create({
  baseURL: dataBaseURL,
  headers: { ...commonHeaders },
});

// auth-клієнт: auth/profile/admin-users на попередньому беку, без country-scope.
export const authClient = axios.create({
  baseURL: authBaseURL,
  headers: { ...commonHeaders },
});

// Мова інтерфейсу — потрібна на обох клієнтах.
export const setAcceptLanguage = (lang: string) => {
  dataClient.defaults.headers['Accept-Language'] = lang;
  authClient.defaults.headers['Accept-Language'] = lang;
};

// Окремий клієнт для refresh — без інтерсепторів, тож 401 на самому refresh
// не може запустити рекурсивний рефреш. Refresh — auth-ендпоінт → auth-бек.
const refreshClient = axios.create({
  baseURL: authBaseURL,
  headers: { ...commonHeaders },
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const flushQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
};

const forceLogout = () => {
  removeToken();
  delete axios.defaults.headers.common['Authorization'];
  window.location.href = '/login';
};

dataClient.interceptors.request.use(
  (config) => {
    config.headers['Authorization'] = getToken();
    // Префіксимо лише раз: на retry після refresh config.url уже переписаний.
    const anyConfig = config as unknown as { _countryScoped?: boolean };
    if (!anyConfig._countryScoped) {
      const path = String(config.url ?? '').replace(/^\/+/, '');
      config.url = `${countryCode}/admin/${path}`;
      anyConfig._countryScoped = true;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

authClient.interceptors.request.use(
  (config) => {
    config.headers['Authorization'] = getToken();
    return config;
  },
  (error) => Promise.reject(error)
);

// Спільна 401 → refresh логіка для обох клієнтів.
const attachRefreshInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }

      const originalConfig = error.config;

      // Захист від нескінченного циклу: один запит — одна спроба рефрешу.
      if (originalConfig._retry) {
        forceLogout();
        return Promise.reject(error);
      }
      originalConfig._retry = true;

      // Рефреш уже виконується — стаємо в чергу і чекаємо новий токен.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalConfig.headers['Authorization'] = `Bearer ${token}`;
              resolve(client.request(originalConfig));
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        const res = await refreshClient.post('auth/refresh', null);
        const newToken = res.data.data.accessToken;
        saveToken(newToken);
        flushQueue(null, newToken);
        originalConfig.headers['Authorization'] = `Bearer ${newToken}`;
        return client.request(originalConfig);
      } catch (e) {
        flushQueue(e, null);
        forceLogout();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

attachRefreshInterceptor(dataClient);
attachRefreshInterceptor(authClient);

export default dataClient;
